import React from 'react';
import { connect } from 'react-redux';
import { Button, Message } from 'semantic-ui-react';
import { EPtwStatus, EUserGroup, EUserLevels, IProject, IPtw, IUser, statusMap } from '@relab/alstom-ptw-shared';

import { ptwEditAction } from '../../redux/actions';
import { PtwRequestTestModal, PtwRequestTestModalValues } from './PtwRequestTestModal';
import { PtwAccettazioneRejectModal, PtwAccettazioneRejectModalValues } from './PtwAccettazioneRejectModal';
import { PtwQualityRejectModal, PtwQualityRejectModalValues } from './PtwQualityRejectModal';
import { PtwQualityTestRequestedModal, PtwQualityTestRequestedModalValues } from './PtwQualityTestRequestedModal';
import { PtwCompletionStatusModal } from './PtwCompletionStatusModal';
import { appState, appStateServerRequests } from '../../redux/selectors';
import { PtwTestRequestedModal } from './PtwTestRequestedModal';
import { PtwWorkCompletionModal } from './PtwWorkCompletionModal';
import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { PtwTestResultModal } from './PtwTestResultModal';
import { PtwAccettazioneConfirmModal } from './PtwAccettazioneConfirmModal';
import { TranslationComponent } from '../TranslationComponent';
import i18n from 'i18next';
import { withTranslation } from 'react-i18next';
import { client } from '../../redux/store';

interface PtwDetailNextComponentProps {
  i18n: typeof i18n;
  t: typeof i18n.t;
  project: IProject;
  ptw: IPtw;
  ptwEditAction: typeof ptwEditAction;
  user: IUser;
  editPtwServerRequest?: ServerRequestStatus;
}

const PPtwDetailNextComponent = ({
  project,
  ptw,
  ptwEditAction,
  user,
  editPtwServerRequest,
}: PtwDetailNextComponentProps) => {
  const ptwUpdate = async (ptwParams: Partial<IPtw>, files?: { [k: string]: File }) => {
    if (files) {
      const data = new FormData();
      for (const filesKey in files) {
        data.append(filesKey, files[filesKey]);
      }

      data.append('ptw', JSON.stringify(ptw));

      await client.post(`ptw/${ptw._id}/file`, data);
    }

    ptwEditAction({ ...ptw, ...ptwParams });
  };

  if (editPtwServerRequest && editPtwServerRequest.error) {
    return <Message error content={editPtwServerRequest.error} />;
  }

  if (user.userLevel !== EUserLevels.Admin) {
    let authorized = false;

    const filteredAssignees = project.assignees.filter((a) => a.user._id === user._id);
    if (
      !!filteredAssignees &&
      statusMap[ptw.status].roles.some((role) => filteredAssignees.map((fa) => fa.group).includes(role))
    ) {
      authorized = authorized || true;
    }

    if (ptw.status === EPtwStatus.VERIFICA_COLLAUDO && ptw.testingAssignee && ptw.testingAssignee._id === user._id) {
      authorized = authorized || true;
    }

    if (
      ptw.status === EPtwStatus.VERIFICA_QUALITA_R &&
      (ptw.qualityAssignees as IUser[]).map((qa) => qa._id).includes(user._id)
    ) {
      authorized = authorized || true;
    }

    if (
      ptw.status === EPtwStatus.ACCETTAZIONE_Y &&
      filteredAssignees.length === 1 &&
      filteredAssignees.find((assignee) => [EUserGroup.PRTCM, EUserGroup.PRTCM_DELEGATE].includes(assignee.group))
    ) {
      authorized = false;
    }

    if (!authorized) {
      return null;
    }
  }

  switch (ptw.status) {
    case EPtwStatus.NUOVO:
      return (
        <React.Fragment>
          <Button primary onClick={() => ptwUpdate({ status: ptw.risk ? EPtwStatus.EHS : EPtwStatus.ACCETTAZIONE_R })}>
            <TranslationComponent stringKey={'Send'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.EHS:
      return (
        <div>
          <TranslationComponent stringKey={'Check EHS'} />
        </div>
      );

    case EPtwStatus.ACCETTAZIONE_R:
      return (
        <React.Fragment>
          <PtwAccettazioneRejectModal
            onSubmit={({ refusalComments }: PtwAccettazioneRejectModalValues) =>
              ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_N, refusalComments })
            }
          />
          <PtwAccettazioneConfirmModal
            onSubmit={({ prtcmNotes }) => {
              ptwUpdate({ prtcmNotes, status: EPtwStatus.VALUTAZIONE_RICOLLAUDO });
            }}
          />
        </React.Fragment>
      );

    case EPtwStatus.ACCETTAZIONE_N:
      return (
        <React.Fragment>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_R })}>
            <TranslationComponent stringKey={'Resend'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.VALUTAZIONE_RICOLLAUDO:
      return (
        <React.Fragment>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_QUALITA_R })}>
            <TranslationComponent stringKey={'Qualità'} />
          </Button>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.CONFERMA_RICOLLAUDO })}>
            <TranslationComponent stringKey={'Richiesto ricollaudo'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.CONFERMA_RICOLLAUDO:
      return (
        <React.Fragment>
          <Button
            primary
            onClick={() => ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_QUALITA_R, requiredTesting: false })}
          >
            <TranslationComponent stringKey={'No test'} />
          </Button>
          <PtwRequestTestModal
            buttonTitle="Request test"
            onSubmit={({ testingNotes, testingSequence }: PtwRequestTestModalValues) =>
              ptwUpdate(
                {
                  status: EPtwStatus.ACCETTAZIONE_QUALITA_R,
                  requiredTesting: true,
                  testingNotes,
                },
                { testingSequence }
              )
            }
          />
        </React.Fragment>
      );

    case EPtwStatus.ACCETTAZIONE_QUALITA_R:
      return (
        <React.Fragment>
          <PtwQualityRejectModal
            onSubmit={({ refusalQualityComments }: PtwQualityRejectModalValues) =>
              ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_QUALITA_N, refusalQualityComments })
            }
          />
          <Button positive primary onClick={() => ptwUpdate({ status: EPtwStatus.CONTROLLO_QUALITA_R })}>
            <TranslationComponent stringKey={'Accept'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.ACCETTAZIONE_QUALITA_N:
      return (
        <React.Fragment>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_R })}>
            <TranslationComponent stringKey={'Resend'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.CONTROLLO_QUALITA_R:
      return (
        <React.Fragment>
          <Button
            primary
            onClick={() => ptwUpdate({ status: EPtwStatus.ACCETTAZIONE_Y, requiredQualityTesting: false })}
          >
            <TranslationComponent stringKey={'No quality test'} />
          </Button>
          <PtwQualityTestRequestedModal
            projectId={project._id}
            onSubmit={(values: PtwQualityTestRequestedModalValues) =>
              ptwUpdate({
                status: EPtwStatus.ACCETTAZIONE_Y,
                requiredQualityTesting: true,
                qualityAssignees: [values.user],
                qualityNotes: values.qualityNotes,
              })
            }
          />
        </React.Fragment>
      );

    case EPtwStatus.ACCETTAZIONE_Y:
      return (
        <React.Fragment>
          {ptw.multiDay && (
            <PtwCompletionStatusModal
              onSubmit={(values) => ptwUpdate({ completionStatus: [...ptw.completionStatus, values.completionStatus] })}
            />
          )}
          <PtwWorkCompletionModal
            onSubmit={({ extraWork, extraWorkNotes }) => {
              if (extraWork) {
                ptwUpdate({ status: EPtwStatus.RICOLLAUDO_EXTRA_R, extraWorkNotes });
              } else {
                let nextState;

                if (ptw.requiredTesting) {
                  nextState = EPtwStatus.VERIFICA_COLLAUDO_R;
                } else if (ptw.requiredQualityTesting) {
                  nextState = EPtwStatus.VERIFICA_QUALITA_R;
                } else {
                  nextState = EPtwStatus.CHIUSURA_R;
                }

                ptwUpdate({ status: nextState });
              }
            }}
          />
        </React.Fragment>
      );

    case EPtwStatus.RICOLLAUDO_EXTRA_R:
      return (
        <React.Fragment>
          <PtwRequestTestModal
            buttonTitle="Edit testing notes"
            values={{ testingNotes: ptw.testingNotes }}
            onSubmit={({ testingNotes }: PtwRequestTestModalValues) =>
              ptwUpdate({ status: EPtwStatus.RICOLLAUDO_EXTRA_R, testingNotes })
            }
          />
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.VERIFICA_COLLAUDO_R })}>
            <TranslationComponent stringKey={'Verifica collaudo'} />
          </Button>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.CHIUSURA_R })}>
            <TranslationComponent stringKey={'Chiudi'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.VERIFICA_COLLAUDO_R:
      return (
        <React.Fragment>
          <PtwTestRequestedModal
            projectId={project._id}
            onSubmit={(values: PtwQualityTestRequestedModalValues) =>
              ptwUpdate({
                status: EPtwStatus.VERIFICA_COLLAUDO,
                testingAssignee: values.user,
              })
            }
          />
        </React.Fragment>
      );

    case EPtwStatus.VERIFICA_COLLAUDO:
      return (
        <PtwTestResultModal
          onSubmit={({ testPassed }) => {
            let nextState;

            if (ptw.requiredQualityTesting) {
              nextState = EPtwStatus.VERIFICA_QUALITA_R;
            } else {
              nextState = EPtwStatus.CHIUSURA_R;
            }

            ptwUpdate({ testPassed, status: nextState });
          }}
        />
      );

    case EPtwStatus.VERIFICA_QUALITA_R:
      return (
        <React.Fragment>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.CHIUSURA_R })}>
            <TranslationComponent stringKey={'Chiudi'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.CHIUSURA_R:
      return (
        <React.Fragment>
          <Button primary onClick={() => ptwUpdate({ status: EPtwStatus.CHIUSURA_Y })}>
            <TranslationComponent stringKey={'Chiudi'} />
          </Button>
        </React.Fragment>
      );

    case EPtwStatus.CHIUSURA_Y:
      return null;

    default:
      return null;
  }
};

export const PtwDetailNextComponent = connect(
  (state: RootState) => ({
    user: appState(state).user,
    editPtwServerRequest: appStateServerRequests(state, ActionType.PTW_EDIT),
  }),
  { ptwEditAction }
)(withTranslation()(PPtwDetailNextComponent));
